import { useQuery } from '@tanstack/react-query';

import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { useCan, useFeature } from '../../stores/session';
import { Verified } from '../../components/ui/Verified';

interface Payment {
  id: number;
  kind: 'tip' | 'boost';
  status: 'paid' | 'refunded';
  /** What the payer was charged. */
  gross_cents: number;
  /** What reached the host. Always the smaller number. */
  host_cents: number;
  created_at: string;
  from: { username: string; display_name: string; verified?: boolean } | null;
}

interface Earnings {
  totals: { count: number; gross_cents: number; host_cents: number };
  items: Payment[];
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/**
 * The running total, for anywhere that wants the figure and not the ledger.
 *
 * Shares its query key with the panel, so a page showing both asks once and the
 * two can never disagree about the number.
 *
 * `null` while it is unknown - payments off, not a host, or the request still in
 * flight - so a caller can leave the space empty rather than render a zero that
 * is really an absence.
 */
export function useEarningsTotal(): number | null {
  const enabled = useFeature('payments');
  const isHost = useCan('host.broadcast');

  const { data } = useQuery({
    queryKey: ['earnings'],
    queryFn: () => api.get<Earnings>('/me/earnings'),
    enabled: enabled && isHost,
  });

  if (!enabled || !isHost || !data) return null;

  return data.totals.host_cents;
}

/** The same formatting the panel uses, for those callers. */
export const formatEarnings = money;

/**
 * The day and month a payment landed. No year, no time.
 *
 * This is a list of the last twenty-five things that happened, read to answer
 * "did that arrive" - a timestamp to the second would be precision nobody is
 * using, and a relative "3 days ago" stops being useful the moment somebody is
 * reconciling against a bank statement.
 */
const when = (iso: string) =>
  new Date(iso.replace(' ', 'T')).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });

/**
 * What a host has been paid.
 *
 * ## Why the big number is the host's share
 *
 * Two numbers exist for every payment - what somebody paid, and what reached
 * the host after processing and the platform's cut - and the one a host cares
 * about is theirs. Leading with the gross would flatter the figure and then
 * disagree with their bank, which is the single worst thing a page like this
 * can do. The gross is still shown, next to it, because a host is entitled to
 * see what was taken and to ask about it.
 *
 * ## Refunds are listed and not counted
 *
 * A boost bought seconds before the host played the track cannot be delivered,
 * so it is refunded - see `StripeWebhookAction`. The host saw a notification for
 * it, so leaving it out entirely would make this page disagree with their
 * memory. Including it in the total would make it disagree with their bank. It
 * appears, struck through, outside the total.
 */
export function EarningsPanel() {
  const enabled = useFeature('payments');
  const isHost = useCan('host.broadcast');

  const { data, isPending } = useQuery({
    queryKey: ['earnings'],
    queryFn: () => api.get<Earnings>('/me/earnings'),
    enabled: enabled && isHost,
  });

  if (!enabled || !isHost) {
    return null;
  }

  return (
    <Card className="p-6" data-earnings-panel>
      <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-faint">Earnings</h2>

      {isPending ? (
        <p className="font-mono text-[10px] text-ghost">loading…</p>
      ) : !data || data.totals.count === 0 ? (
        /*
         * Said as a thing that has not happened yet rather than as a zero.
         *
         * A host who has just connected their account and sees "$0.00" reads it
         * as a number that might be wrong. This is the same fact with the
         * anxiety removed.
         */
        <p className="text-sm text-soft">
          Nothing yet. Tips and boosts from your rooms will show up here, with
          what each one paid you.
        </p>
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-3xl font-semibold tabular-nums text-ink" data-earnings-total>
              {money(data.totals.host_cents)}
            </span>
            <span className="text-sm text-soft">
              paid to you, from {money(data.totals.gross_cents)} across{' '}
              {data.totals.count === 1 ? '1 payment' : `${data.totals.count} payments`}
            </span>
          </div>

          <ul className="flex flex-col">
            {data.items.map((item) => {
              const refunded = item.status === 'refunded';

              return (
                <li
                  key={item.id}
                  data-earning={item.id}
                  className="flex items-center gap-3 border-t border-line py-2.5 text-sm first:border-t-0"
                >
                  <span className="w-12 shrink-0 font-mono text-[11px] text-faint">
                    {when(item.created_at)}
                  </span>

                  <span className="min-w-0 flex-1 truncate text-soft">
                    {item.kind === 'tip' ? 'Tip' : 'Boost'} from{' '}
                    <span className="text-ink">
                      {/* Anonymised when somebody closes their account. The
                          payment outlives the profile - see the migration. */}
                      {item.from?.display_name || item.from?.username || 'a deleted account'}
                      {item.from?.verified && <Verified />}
                    </span>
                  </span>

                  {refunded && (
                    <span className="meta-caps shrink-0 text-hot-text">refunded</span>
                  )}

                  <span
                    className={`shrink-0 tabular-nums ${
                      refunded ? 'text-faint line-through' : 'text-fresh'
                    }`}
                  >
                    {money(item.host_cents)}
                  </span>
                </li>
              );
            })}
          </ul>

          <p className="mt-4 text-xs text-faint">
            Your share of each payment, after processing and the platform&rsquo;s
            cut. Stripe pays out on its own schedule, so what is listed here has
            already left the payer.
          </p>
        </>
      )}
    </Card>
  );
}
